import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/ui-kit";
import {
  ListChecks, Circle, Spinner, CheckCircle, BellRinging, CaretLeft, CaretRight,
  User, Buildings, CalendarBlank,
} from "@phosphor-icons/react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, parseISO,
} from "date-fns";

const kindLabel = { follow_up: "Follow up", payment: "Payment", email: "Email", general: "General" };
const dotColor = { high: "#B91C1C", medium: "#F49B1C", low: "#94A3B8" };
const reminderLabel = (h) => (h < 24 ? `${h}h before` : h === 24 ? "1 day before" : h % 168 === 0 ? `${h / 168}w before` : `${h / 24} days before`);

function Metric({ label, value, sub, icon: Icon, tint, testid }) {
  return (
    <div data-testid={testid} className="card-soft p-5 fade-up">
      <div className="flex items-start justify-between mb-4">
        <span className="font-semibold text-xs uppercase tracking-wide text-slate-500">{label}</span>
        <div className="p-2 rounded-xl" style={{ backgroundColor: tint.bg }}>
          <Icon size={18} weight="fill" color={tint.fg} />
        </div>
      </div>
      <p className="font-heading font-extrabold text-4xl tabular leading-none text-[#16205A]">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-2">{sub}</p>}
    </div>
  );
}

function dueDateTime(t) {
  if (!t.due_date) return null;
  const time = t.due_time || "23:59";
  return new Date(`${String(t.due_date).slice(0, 10)}T${time}:00`);
}

export default function Dashboard() {
  const [tasks, setTasks] = useState([]);
  const [stdReminders, setStdReminders] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/tasks").then(({ data }) => setTasks(data));
    api.get("/reminders").then(({ data }) => setStdReminders(data.filter((r) => !r.done)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const advance = async (t, next) => {
    await api.put(`/tasks/${t.id}`, { status: next });
    load();
  };

  const notDone = tasks.filter((t) => t.status === "todo").length;
  const inProgress = tasks.filter((t) => t.status === "in_progress").length;
  const completed = tasks.filter((t) => t.status === "done").length;

  // active task reminders (lead-time reached, task not done, not past due by > offset window)
  const now = new Date();
  const taskReminders = [];
  tasks.forEach((t) => {
    if (t.status === "done" || !Array.isArray(t.reminders) || t.reminders.length === 0) return;
    const due = dueDateTime(t);
    if (!due) return;
    t.reminders.forEach((h) => {
      const trigger = new Date(due.getTime() - h * 3600 * 1000);
      if (now >= trigger && now <= due) taskReminders.push({ task: t, hours: h, due });
    });
  });
  taskReminders.sort((a, b) => a.due - b.due);

  const remindersDue = stdReminders.filter((r) => String(r.due_date).slice(0, 10) <= format(now, "yyyy-MM-dd")).length + taskReminders.length;

  // calendar
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  });
  const tasksOn = (day) => tasks.filter((t) => t.due_date && isSameDay(parseISO(String(t.due_date).slice(0, 10)), day));
  const selectedTasks = tasksOn(selected);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Task overview · Betonwapening Veeris 2.0" />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <Metric testid="metric-notdone" label="Not Done" value={notDone} sub="Tasks to start" icon={Circle} tint={{ bg: "#E2E8F0", fg: "#334155" }} />
          <Metric testid="metric-inprogress" label="In Progress" value={inProgress} sub="Currently being worked on" icon={Spinner} tint={{ bg: "#FEF3C7", fg: "#B45309" }} />
          <Metric testid="metric-completed" label="Completed" value={completed} sub="Tasks finished" icon={CheckCircle} tint={{ bg: "#DCFCE7", fg: "#15803D" }} />
          <Metric testid="metric-reminders" label="Reminders Due" value={remindersDue} sub="Needs your attention" icon={BellRinging} tint={{ bg: "#E0E7FF", fg: "#3730A3" }} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 card-soft p-6 fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-extrabold text-xl text-[#16205A]">{format(month, "MMMM yyyy")}</h3>
              <div className="flex gap-2">
                <button data-testid="cal-prev" onClick={() => setMonth(addMonths(month, -1))} className="w-9 h-9 rounded-lg border border-slate-300 flex items-center justify-center hover:border-[#F49B1C] transition-colors">
                  <CaretLeft size={16} weight="bold" />
                </button>
                <button data-testid="cal-today" onClick={() => { setMonth(new Date()); setSelected(new Date()); }} className="px-3 h-9 rounded-lg border border-slate-300 text-xs font-semibold hover:border-[#F49B1C] transition-colors">Today</button>
                <button data-testid="cal-next" onClick={() => setMonth(addMonths(month, 1))} className="w-9 h-9 rounded-lg border border-slate-300 flex items-center justify-center hover:border-[#F49B1C] transition-colors">
                  <CaretRight size={16} weight="bold" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="text-center font-mono text-[10px] uppercase tracking-wide text-slate-400 py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {days.map((day) => {
                const dayTasks = tasksOn(day);
                const inMonth = isSameMonth(day, month);
                const isSel = isSameDay(day, selected);
                const isToday = isSameDay(day, now);
                return (
                  <button
                    key={day.toISOString()}
                    data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
                    onClick={() => setSelected(day)}
                    className={`aspect-square rounded-xl border p-1.5 flex flex-col items-center justify-start transition-all ${
                      isSel ? "border-[#F49B1C] bg-orange-50 ring-2 ring-[#F49B1C]/30" : "border-slate-200 hover:border-slate-300"
                    } ${!inMonth ? "opacity-40" : ""}`}
                  >
                    <span className={`font-mono text-xs ${isToday ? "bg-[#16205A] text-white w-6 h-6 rounded-full flex items-center justify-center font-bold" : "text-slate-600"}`}>
                      {format(day, "d")}
                    </span>
                    {dayTasks.length > 0 && (
                      <div className="flex gap-0.5 mt-1 flex-wrap justify-center">
                        {dayTasks.slice(0, 3).map((t) => (
                          <span key={t.id} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.status === "done" ? "#22C55E" : dotColor[t.priority] }} />
                        ))}
                        {dayTasks.length > 3 && <span className="font-mono text-[8px] text-slate-400">+{dayTasks.length - 3}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected day tasks */}
          <div className="card-soft p-6 fade-up">
            <h3 className="font-heading font-extrabold text-lg text-[#16205A] flex items-center gap-2">
              <CalendarBlank size={18} weight="fill" className="text-[#F49B1C]" /> {format(selected, "EEE, d MMM")}
            </h3>
            <p className="text-xs text-slate-400 mb-4">{selectedTasks.length} task(s) due</p>
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {selectedTasks.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nothing due this day.</p>}
              {selectedTasks.map((t) => (
                <div key={t.id} data-testid={`day-task-${t.id}`} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm text-slate-800">{t.title}</p>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-xs font-mono text-slate-400">
                    {t.due_time && <span>{t.due_time}</span>}
                    <span className="flex items-center gap-1"><User size={12} weight="fill" /> {t.worker_name || "Unassigned"}</span>
                    {t.customer_name && <span className="flex items-center gap-1"><Buildings size={12} weight="fill" /> {t.customer_name}</span>}
                  </div>
                  {t.status !== "done" && (
                    <button
                      data-testid={`day-advance-${t.id}`}
                      onClick={() => advance(t, t.status === "todo" ? "in_progress" : "done")}
                      className="mt-2 text-[10px] font-semibold uppercase rounded-md border border-slate-300 px-2.5 py-1 hover:bg-[#F49B1C] hover:text-white hover:border-[#F49B1C] transition-colors"
                    >
                      Advance
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Task reminders */}
          <div className="card-soft p-6 fade-up">
            <h3 className="font-heading font-extrabold text-xl mb-4 text-[#16205A]">Task Reminders</h3>
            {taskReminders.length === 0 && <p className="text-sm text-slate-500">No task reminders active right now.</p>}
            <div className="space-y-2">
              {taskReminders.map((r, i) => (
                <div key={`${r.task.id}-${r.hours}-${i}`} className="flex items-center justify-between px-3.5 py-3 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-50"><BellRinging size={16} weight="fill" color="#3730A3" /></div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{r.task.title}</p>
                      <p className="font-mono text-xs text-slate-400">Due {format(r.due, "d MMM, HH:mm")} · {reminderLabel(r.hours)}</p>
                    </div>
                  </div>
                  <StatusBadge status={r.task.priority} />
                </div>
              ))}
            </div>
          </div>

          {/* Standalone reminders */}
          <div className="card-soft p-6 fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-extrabold text-xl text-[#16205A]">Follow-up Reminders</h3>
              <button data-testid="dashboard-view-reminders" onClick={() => navigate("/reminders")} className="text-xs font-semibold text-[#F49B1C] hover:underline">View all</button>
            </div>
            {stdReminders.length === 0 && <p className="text-sm text-slate-500">No follow-up reminders.</p>}
            <div className="space-y-2">
              {stdReminders.slice(0, 6).map((r) => (
                <div key={r.id} className="flex items-center justify-between px-3.5 py-3 rounded-xl border border-slate-200">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{r.title}</p>
                    <p className="font-mono text-xs text-slate-400">{r.customer_name ? `${r.customer_name} · ` : ""}{new Date(r.due_date).toLocaleDateString()}</p>
                  </div>
                  <StatusBadge status={r.kind} label={kindLabel[r.kind]} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
